/**
 * 2026 expansion: kecamatan for previously-uncovered kota.
 * Coordinates are kantor kecamatan / pusat kecamatan (±0.01°).
 * Slugs disambiguated with `-<kota>` suffix when name collides with
 * an existing kecamatan in another kota.
 */
import type { Location } from "./locations";

const W: Location["tz"] = "WIB";
const T: Location["tz"] = "WITA";
const E: Location["tz"] = "WIT";

function k(
  slug: string,
  name: string,
  parentSlug: string,
  provinceSlug: string,
  lat: number,
  lng: number,
  tz: Location["tz"],
): Location {
  return { kind: "kecamatan", slug, name, parentSlug, provinceSlug, lat, lng, tz };
}

export const KECAMATAN_EXPANSION_2026: Location[] = [
  // === Cilegon (Banten) ===
  k("cilegon-kec", "Cilegon", "cilegon", "banten", -6.0025, 106.0111, W),
  k("citangkil", "Citangkil", "cilegon", "banten", -6.0167, 106.0333, W),
  k("ciwandan", "Ciwandan", "cilegon", "banten", -6.0167, 105.95, W),
  k("pulomerak", "Pulomerak", "cilegon", "banten", -5.9333, 106.0, W),
  k("grogol-cilegon", "Grogol", "cilegon", "banten", -5.9667, 106.0167, W),
  k("jombang-cilegon", "Jombang", "cilegon", "banten", -6.0, 106.0, W),

  // === Cimahi (Jawa Barat) ===
  k("cimahi-utara", "Cimahi Utara", "cimahi", "jawa-barat", -6.85, 107.5333, W),
  k("cimahi-tengah", "Cimahi Tengah", "cimahi", "jawa-barat", -6.8722, 107.5425, W),
  k("cimahi-selatan", "Cimahi Selatan", "cimahi", "jawa-barat", -6.9, 107.55, W),

  // === Tegal (Jawa Tengah) ===
  k("tegal-barat", "Tegal Barat", "tegal", "jawa-tengah", -6.8694, 109.1167, W),
  k("tegal-timur", "Tegal Timur", "tegal", "jawa-tengah", -6.8722, 109.1444, W),
  k("tegal-selatan", "Tegal Selatan", "tegal", "jawa-tengah", -6.8833, 109.1333, W),
  k("margadana", "Margadana", "tegal", "jawa-tengah", -6.85, 109.1, W),

  // === Salatiga (Jawa Tengah) ===
  k("sidorejo", "Sidorejo", "salatiga", "jawa-tengah", -7.3167, 110.4833, W),
  k("tingkir", "Tingkir", "salatiga", "jawa-tengah", -7.35, 110.5167, W),
  k("argomulyo", "Argomulyo", "salatiga", "jawa-tengah", -7.3667, 110.4833, W),
  k("sidomukti", "Sidomukti", "salatiga", "jawa-tengah", -7.3333, 110.4667, W),

  // === Madiun (Jawa Timur) ===
  k("kartoharjo", "Kartoharjo", "madiun", "jawa-timur", -7.6286, 111.5328, W),
  k("manguharjo", "Manguharjo", "madiun", "jawa-timur", -7.6167, 111.5167, W),
  k("taman-madiun", "Taman", "madiun", "jawa-timur", -7.6333, 111.5167, W),

  // === Probolinggo (Jawa Timur) ===
  k("kanigaran", "Kanigaran", "probolinggo", "jawa-timur", -7.7667, 113.2167, W),
  k("mayangan", "Mayangan", "probolinggo", "jawa-timur", -7.7444, 113.2167, W),
  k("kademangan-probolinggo", "Kademangan", "probolinggo", "jawa-timur", -7.7833, 113.2, W),
  k("kedopok", "Kedopok", "probolinggo", "jawa-timur", -7.7667, 113.1833, W),
  k("wonoasih", "Wonoasih", "probolinggo", "jawa-timur", -7.7833, 113.2167, W),

  // === Pasuruan (Jawa Timur) ===
  k("gadingrejo-pasuruan", "Gadingrejo", "pasuruan", "jawa-timur", -7.65, 112.9, W),
  k("purworejo-pasuruan", "Purworejo", "pasuruan", "jawa-timur", -7.6333, 112.9, W),
  k("bugulkidul", "Bugulkidul", "pasuruan", "jawa-timur", -7.65, 112.9167, W),
  k("panggungrejo-pasuruan", "Panggungrejo", "pasuruan", "jawa-timur", -7.6333, 112.9167, W),

  // === Mojokerto (Jawa Timur) ===
  k("magersari", "Magersari", "mojokerto", "jawa-timur", -7.4667, 112.4333, W),
  k("kranggan", "Kranggan", "mojokerto", "jawa-timur", -7.4667, 112.4333, W),
  k("prajurit-kulon", "Prajurit Kulon", "mojokerto", "jawa-timur", -7.4667, 112.4167, W),

  // === Kediri (Jawa Timur) ===
  k("mojoroto", "Mojoroto", "kediri", "jawa-timur", -7.8167, 112.0, W),
  k("kota-kediri", "Kota Kediri", "kediri", "jawa-timur", -7.8167, 112.0167, W),
  k("pesantren", "Pesantren", "kediri", "jawa-timur", -7.85, 112.0167, W),

  // === Blitar (Jawa Timur) ===
  k("sukorejo-blitar", "Sukorejo", "blitar", "jawa-timur", -8.1, 112.1667, W),
  k("kepanjen-kidul", "Kepanjenkidul", "blitar", "jawa-timur", -8.0917, 112.1631, W),
  k("sananwetan", "Sananwetan", "blitar", "jawa-timur", -8.1, 112.1833, W),

  // === Batu (Jawa Timur) ===
  k("batu-kec", "Batu", "batu", "jawa-timur", -7.8717, 112.5275, W),
  k("junrejo", "Junrejo", "batu", "jawa-timur", -7.9, 112.55, W),
  k("bumiaji", "Bumiaji", "batu", "jawa-timur", -7.8333, 112.5333, W),

  // === Banjar (Jawa Barat) ===
  k("banjar-kec", "Banjar", "banjar", "jawa-barat", -7.3667, 108.5333, W),
  k("purwaharja", "Purwaharja", "banjar", "jawa-barat", -7.3333, 108.5333, W),
  k("pataruman", "Pataruman", "banjar", "jawa-barat", -7.3833, 108.5333, W),
  k("langensari", "Langensari", "banjar", "jawa-barat", -7.3833, 108.55, W),

  // === Pangkal Pinang (Bangka Belitung) ===
  k("taman-sari-pangkal", "Taman Sari", "pangkal-pinang", "bangka-belitung", -2.1333, 106.1167, W),
  k("pangkalbalam", "Pangkalbalam", "pangkal-pinang", "bangka-belitung", -2.1, 106.1333, W),
  k("rangkui", "Rangkui", "pangkal-pinang", "bangka-belitung", -2.1333, 106.1, W),
  k("bukit-intan", "Bukit Intan", "pangkal-pinang", "bangka-belitung", -2.15, 106.1333, W),
  k("girimaya", "Girimaya", "pangkal-pinang", "bangka-belitung", -2.1167, 106.1167, W),
  k("gerunggang", "Gerunggang", "pangkal-pinang", "bangka-belitung", -2.1167, 106.1, W),

  // === Tanjung Pinang (Kepulauan Riau) ===
  k("tanjungpinang-kota", "Tanjungpinang Kota", "tanjung-pinang", "kepulauan-riau", 0.9167, 104.45, W),
  k("tanjungpinang-barat", "Tanjungpinang Barat", "tanjung-pinang", "kepulauan-riau", 0.9167, 104.4333, W),
  k("tanjungpinang-timur", "Tanjungpinang Timur", "tanjung-pinang", "kepulauan-riau", 0.9167, 104.4667, W),
  k("bukit-bestari", "Bukit Bestari", "tanjung-pinang", "kepulauan-riau", 0.9, 104.45, W),

  // === Singkawang (Kalimantan Barat) ===
  k("singkawang-tengah", "Singkawang Tengah", "singkawang", "kalimantan-barat", 0.9, 108.9833, W),
  k("singkawang-utara", "Singkawang Utara", "singkawang", "kalimantan-barat", 0.95, 108.9833, W),
  k("singkawang-selatan", "Singkawang Selatan", "singkawang", "kalimantan-barat", 0.85, 108.9833, W),
  k("singkawang-barat", "Singkawang Barat", "singkawang", "kalimantan-barat", 0.9, 108.9667, W),
  k("singkawang-timur", "Singkawang Timur", "singkawang", "kalimantan-barat", 0.9, 109.0167, W),

  // === Palu (Sulawesi Tengah) ===
  k("palu-barat", "Palu Barat", "palu", "sulawesi-tengah", -0.9, 119.8333, T),
  k("palu-timur", "Palu Timur", "palu", "sulawesi-tengah", -0.9, 119.8667, T),
  k("palu-utara", "Palu Utara", "palu", "sulawesi-tengah", -0.85, 119.85, T),
  k("palu-selatan", "Palu Selatan", "palu", "sulawesi-tengah", -0.95, 119.85, T),
  k("mantikulore", "Mantikulore", "palu", "sulawesi-tengah", -0.9, 119.9, T),
  k("tatanga", "Tatanga", "palu", "sulawesi-tengah", -0.95, 119.85, T),
  k("ulujadi", "Ulujadi", "palu", "sulawesi-tengah", -0.9333, 119.8167, T),
  k("tawaeli", "Tawaeli", "palu", "sulawesi-tengah", -0.7667, 119.8333, T),

  // === Kendari (Sulawesi Tenggara) ===
  k("kendari-kec", "Kendari", "kendari", "sulawesi-tenggara", -3.9667, 122.6, T),
  k("kendari-barat", "Kendari Barat", "kendari", "sulawesi-tenggara", -3.9833, 122.5333, T),
  k("mandonga", "Mandonga", "kendari", "sulawesi-tenggara", -3.9667, 122.5333, T),
  k("baruga", "Baruga", "kendari", "sulawesi-tenggara", -4.0, 122.55, T),
  k("kambu", "Kambu", "kendari", "sulawesi-tenggara", -3.9833, 122.55, T),
  k("poasia", "Poasia", "kendari", "sulawesi-tenggara", -4.0, 122.5667, T),

  // === Ternate (Maluku Utara) ===
  k("ternate-tengah", "Ternate Tengah", "ternate", "maluku-utara", 0.7833, 127.3833, E),
  k("ternate-utara", "Ternate Utara", "ternate", "maluku-utara", 0.8, 127.3833, E),
  k("ternate-selatan", "Ternate Selatan", "ternate", "maluku-utara", 0.75, 127.3667, E),
  k("pulau-ternate", "Pulau Ternate", "ternate", "maluku-utara", 0.8, 127.3333, E),
  k("moti", "Moti", "ternate", "maluku-utara", 0.4667, 127.4, E),

  // === Ambon (Maluku) ===
  k("nusaniwe", "Nusaniwe", "ambon", "maluku", -3.7, 128.1667, E),
  k("sirimau", "Sirimau", "ambon", "maluku", -3.6833, 128.2, E),
  k("teluk-ambon", "Teluk Ambon", "ambon", "maluku", -3.65, 128.15, E),
  k("baguala", "Baguala", "ambon", "maluku", -3.65, 128.2333, E),
  k("leitimur-selatan", "Leitimur Selatan", "ambon", "maluku", -3.7333, 128.2333, E),

  // === Sorong (Papua Barat Daya) ===
  k("sorong-kec", "Sorong", "sorong", "papua-barat-daya", -0.8833, 131.25, E),
  k("sorong-barat", "Sorong Barat", "sorong", "papua-barat-daya", -0.8833, 131.2333, E),
  k("sorong-timur", "Sorong Timur", "sorong", "papua-barat-daya", -0.8833, 131.2833, E),
  k("sorong-utara", "Sorong Utara", "sorong", "papua-barat-daya", -0.85, 131.25, E),

  // === Manokwari (Papua Barat) ===
  k("manokwari-barat", "Manokwari Barat", "manokwari", "papua-barat", -0.8615, 134.0620, E),
  k("manokwari-timur", "Manokwari Timur", "manokwari", "papua-barat", -0.85, 134.1, E),
  k("manokwari-utara", "Manokwari Utara", "manokwari", "papua-barat", -0.7833, 134.0333, E),
  k("manokwari-selatan", "Manokwari Selatan", "manokwari", "papua-barat", -0.95, 134.05, E),
];